/**
 * useTreeGridState Hook
 *
 * Main state management hook for TreeGrid component.
 * Handles tree structure, selection, editing, and keyboard navigation.
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { TreeGridRow, TreeGridConfig, DragState } from "../types";
import {
  flattenTree,
  buildTree,
  moveRow,
  indentRow,
  outdentRow,
  toggleExpansion,
  getDescendantIds,
} from "../utils/treeOperations";
import {
  toggleRowSelection,
  selectAll,
  deselectAll,
  getSelectionState,
  areAllVisibleRowsSelected,
  areSomeVisibleRowsSelected,
  handleSelectionAfterMove,
} from "../utils/selectionUtils";
import {
  validateCellValue,
  setCellError,
  clearCellError,
} from "../utils/validationUtils";

export function useTreeGridState<T = any>(config: TreeGridConfig<T>) {
  const {
    data: initialData,
    columns,
    showSelection = false,
    showSelectAll = false,
    enableDragDrop = true,
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    enableKeyboardNav = true,
    enableInlineEdit = true,
    defaultExpanded = false,
    currentPage = 1,
    pageSize = 50,
    totalRows,
    paginationMode = "client",
    totalPages: serverTotalPages,
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    indentPerLevel = 24,
    maxDepth = 10,
    onRowCreate,
    onRowUpdate,
    onRowDelete,
    onEditCancel,
    onRowMove,
    onSelectionChange,
    onExpandCollapse,
    onCellEdit,
    onCellValidationError,
    onCellValueChange,
    onMultiFieldUpdate,
    defaultEditingRowId,
    getNewRowDefaultData,
    defaultSelectedIds,
  } = config;

  // Core state
  const [rows, setRows] = useState<TreeGridRow<T>[]>(() =>
    buildTree(initialData),
  );
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (defaultExpanded) {
      initialData
        .filter((r) => r.childrenIds.length > 0)
        .forEach((r) => initial.add(r.id));
    }
    return initial;
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Record<string, any>>({});
  // Ref to track latest editing values synchronously (for immediate commits after multi-field changes)
  const editingValuesRef = useRef<Record<string, any>>({});
  const [focusedCell, setFocusedCell] = useState<{
    rowId: string;
    columnKey: string;
  } | null>(null);
  const [dragState, setDragState] = useState<DragState | null>(null);
  const [validationErrors, setValidationErrors] = useState<
    Map<string, Map<string, string>>
  >(new Map());
  const [page, setPage] = useState(currentPage);

  // Track pending edit for newly created rows - using ref to survive re-renders
  // Derived state
  const rowsById = useMemo(() => {
    return new Map(rows.map((row) => [row.id, row]));
  }, [rows]);

  const visibleRows = useMemo(() => {
    const flattened = flattenTree(rows, expandedIds);

    // Server-side pagination: data is already paginated by the server
    if (paginationMode === "server") {
      return flattened;
    }

    // Client-side pagination: slice the data locally
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return flattened.slice(start, end);
  }, [rows, expandedIds, page, pageSize, paginationMode]);

  const totalPages = useMemo(() => {
    // Server-side pagination: use provided total pages
    if (paginationMode === "server") {
      if (serverTotalPages !== undefined) {
        return serverTotalPages;
      }
      // Fallback: calculate from totalRows
      if (totalRows !== undefined) {
        return Math.ceil(totalRows / pageSize);
      }
      return 1;
    }

    // Client-side pagination: calculate from local data
    const totalVisible = flattenTree(rows, expandedIds).length;
    return Math.ceil(totalVisible / pageSize);
  }, [
    rows,
    expandedIds,
    pageSize,
    paginationMode,
    serverTotalPages,
    totalRows,
  ]);

  const allSelected = useMemo(() => {
    return areAllVisibleRowsSelected(visibleRows, selectedIds);
  }, [visibleRows, selectedIds]);

  const someSelected = useMemo(() => {
    return areSomeVisibleRowsSelected(visibleRows, selectedIds);
  }, [visibleRows, selectedIds]);

  // Ref to avoid re-applying default selection when only defaultSelectedIds reference changes.
  // Use null on init so that when TreeGrid first mounts with data (after loader), we apply default selection.
  const prevInitialDataRef = useRef<typeof initialData | null>(null);

  // Sync rows with data prop changes
  useEffect(() => {
    const newRows = buildTree(initialData);

    // Preserve locally created rows (rows with temp IDs that don't exist in initialData)
    setRows((prevRows) => {
      const initialDataIds = new Set(initialData.map((r) => r.id));
      // Find rows that were created locally by TreeGrid (not in initialData)
      // Only preserve rows with "row_" prefix (from handleCreateRow - internal creation)
      // Do NOT preserve rows with "new-" prefix - those are created externally by the parent
      // and should follow the parent's initialData (if parent removes them, they should be gone)
      const locallyCreatedRows = prevRows.filter(
        (r) => r.id.startsWith("row_") && !initialDataIds.has(r.id),
      );

      // If there are locally created rows, preserve them
      if (locallyCreatedRows.length > 0) {
        // Merge: new rows from initialData + locally created rows
        return [...newRows, ...locallyCreatedRows];
      }

      return newRows;
    });

    // Sync expandedIds based on isExpanded property of rows
    setExpandedIds((prevExpandedIds) => {
      const newExpandedIds = new Set(prevExpandedIds);
      newRows.forEach((row) => {
        if (row.isExpanded && row.childrenIds.length > 0) {
          newExpandedIds.add(row.id);
        }
      });
      return newExpandedIds;
    });

    // Apply default selection when data changed or on first mount with data (TreeGrid mounts after loader hides)
    const dataChanged =
      prevInitialDataRef.current === null ||
      prevInitialDataRef.current !== initialData;
    const newRowsById = new Map(newRows.map((r) => [r.id, r]));
    const idsToSelect = defaultSelectedIds
      ? defaultSelectedIds.filter((id) => newRowsById.has(id))
      : [];
    if (dataChanged && defaultSelectedIds && defaultSelectedIds.length > 0) {
      prevInitialDataRef.current = initialData;
      if (idsToSelect.length > 0) {
        const newSelectedIds = new Set(idsToSelect);
        setSelectedIds(newSelectedIds);
        onSelectionChange?.(Array.from(newSelectedIds));
      }
    } else if (dataChanged) {
      prevInitialDataRef.current = initialData;
    }
  }, [initialData, defaultSelectedIds, onSelectionChange]);

  // Auto-start edit mode for defaultEditingRowId
  useEffect(() => {
    // console.log("[TreeGrid] Auto-edit effect:", {
    //   defaultEditingRowId,
    //   enableInlineEdit,
    //   hasRowInMap: defaultEditingRowId
    //     ? rowsById.has(defaultEditingRowId)
    //     : false,
    //   editingRowId,
    //   rowsCount: rowsById.size,
    // });

    if (defaultEditingRowId && enableInlineEdit) {
      const row = rowsById.get(defaultEditingRowId);
      if (row && editingRowId !== defaultEditingRowId) {
        // Find first editable column
        const firstEditableColumn = columns.find((c) => c.editable);
        if (firstEditableColumn) {
          // console.log(
          //   "[TreeGrid] Starting edit mode for:",
          //   defaultEditingRowId,
          // );
          // Initialize editing values from row data
          const initialValues: Record<string, any> = {};
          columns.forEach((col) => {
            initialValues[col.key] = (row.data as any)[col.key];
          });

          setEditingRowId(defaultEditingRowId);
          setEditingValues(initialValues);
          editingValuesRef.current = initialValues;
          setFocusedCell({
            rowId: defaultEditingRowId,
            columnKey: firstEditableColumn.key,
          });
        }
      }
    }
  }, [defaultEditingRowId, enableInlineEdit, rowsById, columns, editingRowId]);

  // Expand/Collapse operations
  const handleToggleExpand = useCallback(
    (rowId: string) => {
      const row = rowsById.get(rowId);
      if (!row || row.childrenIds.length === 0) return;

      const newExpandedIds = toggleExpansion(rowId, expandedIds);
      setExpandedIds(newExpandedIds);

      // Update row isExpanded state
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId ? { ...r, isExpanded: newExpandedIds.has(rowId) } : r,
        ),
      );

      onExpandCollapse?.(rowId, newExpandedIds.has(rowId));
    },
    [rowsById, expandedIds, onExpandCollapse],
  );

  const handleExpandAll = useCallback(() => {
    const allRowsWithChildren = rows.filter((r) => r.childrenIds.length > 0);
    const newExpandedIds = new Set(allRowsWithChildren.map((r) => r.id));
    setExpandedIds(newExpandedIds);
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        isExpanded: newExpandedIds.has(r.id),
      })),
    );
  }, [rows]);

  const handleCollapseAll = useCallback(() => {
    setExpandedIds(new Set());
    setRows((prev) => prev.map((r) => ({ ...r, isExpanded: false })));
  }, []);

  // Selection operations
  const handleToggleSelection = useCallback(
    (rowId: string) => {
      if (!showSelection) return;

      const newSelectedIds = toggleRowSelection(
        rowId,
        selectedIds,
        rowsById,
        true,
      );
      setSelectedIds(newSelectedIds);
      onSelectionChange?.(Array.from(newSelectedIds));
    },
    [showSelection, selectedIds, rowsById, onSelectionChange],
  );

  const handleSelectAll = useCallback(() => {
    if (!showSelection || !showSelectAll) return;

    if (allSelected) {
      const newSelectedIds = deselectAll();
      setSelectedIds(newSelectedIds);
      onSelectionChange?.([]);
    } else {
      const newSelectedIds = selectAll(visibleRows);
      setSelectedIds(newSelectedIds);
      onSelectionChange?.(Array.from(newSelectedIds));
    }
  }, [
    showSelection,
    showSelectAll,
    allSelected,
    visibleRows,
    onSelectionChange,
  ]);

  const handleClearSelection = useCallback(() => {
    const newSelectedIds = deselectAll();
    setSelectedIds(newSelectedIds);
    onSelectionChange?.([]);
  }, [onSelectionChange]);

  // Cell editing operations
  const handleStartEdit = useCallback(
    (rowId: string, columnKey: string) => {
      if (!enableInlineEdit) {
        return;
      }

      const row = rowsById.get(rowId);
      if (!row) {
        return;
      }

      // Initialize editing values with current row data
      const initialValues: Record<string, any> = {};
      columns
        .filter((c) => c.editable)
        .forEach((col) => {
          initialValues[col.key] = (row.data as any)[col.key];
        });

      setEditingRowId(rowId);
      setEditingValues(initialValues);
      editingValuesRef.current = initialValues;
      setFocusedCell({ rowId, columnKey });
    },
    [enableInlineEdit, columns, rowsById],
  );

  const handleCancelEdit = useCallback(() => {
    // Get the row ID to cancel - prefer editingRowId, fallback to defaultEditingRowId
    const rowIdToCancel = editingRowId || defaultEditingRowId;

    // console.log("[TreeGrid] handleCancelEdit called:", {
    //   editingRowId,
    //   defaultEditingRowId,
    //   rowIdToCancel,
    //   hasOnEditCancel: !!onEditCancel,
    // });

    if (rowIdToCancel) {
      const row = rowsById.get(rowIdToCancel);
      // Always call onEditCancel when cancelling an edit, even if row is not found
      // This ensures the parent can clean up new rows that haven't been saved
      //console.log("[TreeGrid] Calling onEditCancel with:", rowIdToCancel);
      onEditCancel?.(rowIdToCancel, row);

      // If this is a new row created via Cmd+Enter (row_ prefix), remove it from internal state
      if (rowIdToCancel.startsWith("row_")) {
        setRows((prev) => prev.filter((r) => r.id !== rowIdToCancel));
      }
    }
    setEditingRowId(null);
    setEditingValues({});
    editingValuesRef.current = {};
  }, [editingRowId, defaultEditingRowId, rowsById, onEditCancel]);

  const handleCommitEdit = useCallback(() => {
    if (!editingRowId) return;

    const row = rowsById.get(editingRowId);
    if (!row) return;

    // Use ref to get latest editing values (includes pending multi-field changes)
    const latestEditingValues = editingValuesRef.current;

    // Debug logging
    // console.log("[TreeGrid] Committing edit:", {
    //   rowId: editingRowId,
    //   editingValues,
    //   currentRowData: row.data,
    // });

    // Validate all edited fields
    let hasErrors = false;
    let newErrors = validationErrors;
    const editableColumns = columns.filter((c) => c.editable);

    for (const column of editableColumns) {
      const value = latestEditingValues[column.key];
      const validation = validateCellValue(value, column, row);

      if (!validation.isValid && validation.error) {
        newErrors = setCellError(
          editingRowId,
          column.key,
          validation.error,
          newErrors,
        );
        onCellValidationError?.(editingRowId, column.key, validation.error);
        hasErrors = true;
      } else {
        newErrors = clearCellError(editingRowId, column.key, newErrors);
      }
    }

    setValidationErrors(newErrors);

    if (hasErrors) {
      return;
    }

    // Update row data with all edited values
    const updatedRow = {
      ...row,
      data: {
        ...row.data,
        ...latestEditingValues,
      },
    };

    //console.log("[TreeGrid] Updated row data:", updatedRow.data);

    setRows((prev) =>
      prev.map((r) => (r.id === editingRowId ? updatedRow : r)),
    );

    // Call callbacks for each edited field
    editableColumns.forEach((column) => {
      const value = latestEditingValues[column.key];
      column.onCommit?.(value, updatedRow);
      onCellEdit?.(editingRowId, column.key, value);
    });

    // Clear editing state — keep focusedCell so the row stays highlighted
    // after the user presses Enter to confirm. Focus is only cleared when the
    // user explicitly tabs away from the grid.
    setEditingRowId(null);
    setEditingValues({});
    editingValuesRef.current = {}; // Clear ref

    onRowUpdate?.(updatedRow);
  }, [
    editingRowId,
    editingValues,
    rowsById,
    columns,
    validationErrors,
    onCellEdit,
    onCellValidationError,
    onRowUpdate,
  ]);

  const handleCellValueChange = useCallback(
    (columnKey: string, value: any) => {
      setEditingValues((prev) => {
        const updated = {
          ...prev,
          [columnKey]: value,
        };
        editingValuesRef.current = updated; // Update ref synchronously
        return updated;
      });

      // Call callback immediately during typing
      if (onCellValueChange && editingRowId) {
        onCellValueChange(editingRowId, columnKey, value);
      }
    },
    [onCellValueChange, editingRowId],
  );

  /**
   * Update multiple cell values at once.
   * Useful for ComboBox selections that update related fields (e.g., partNumber, productName, unitPrice).
   */
  const handleMultiFieldChange = useCallback(
    (changes: Record<string, any>) => {
      setEditingValues((prev) => {
        const updated = {
          ...prev,
          ...changes,
        };
        editingValuesRef.current = updated; // Update ref synchronously
        return updated;
      });

      // Call parent callback immediately with the changes
      if (onMultiFieldUpdate && editingRowId) {
        const row = rowsById.get(editingRowId);
        if (row) {
          onMultiFieldUpdate(editingRowId, changes as Partial<T>, row);
        }
      }
    },
    [onMultiFieldUpdate, editingRowId, rowsById, editingValues],
  );

  // Row operations
  const handleCreateRow = useCallback(
    (parentId: string | null, rowType: "simple" | "product") => {
      // Get default data from parent component if provided
      const defaultData = getNewRowDefaultData?.() ?? ({} as Partial<T>);

      const newRow: TreeGridRow<T> = {
        id: `row_${Date.now()}_${Math.random()}`,
        parentId,
        depth: parentId ? (rowsById.get(parentId)?.depth || 0) + 1 : 0,
        childrenIds: [],
        isExpanded: false,
        rowType,
        data: { ...defaultData } as T,
        sortOrder: parentId
          ? rowsById.get(parentId)?.childrenIds.length || 0
          : rows.filter((r) => r.parentId === null).length,
      };

      // Find first editable column
      const firstEditableColumn = columns.find((c) => c.editable);
      const targetColumn = firstEditableColumn || columns[0];

      // Initialize editing values with default data for all editable columns
      const initialEditingValues: Record<string, any> = {};
      columns
        .filter((c) => c.editable)
        .forEach((col) => {
          initialEditingValues[col.key] = (newRow.data as any)[col.key] ?? "";
        });

      // Update rows immutably - add new row and update parent's children if needed
      if (parentId) {
        setRows((prev) => {
          const updatedRows = prev.map((r) => {
            if (r.id === parentId) {
              return {
                ...r,
                childrenIds: [...r.childrenIds, newRow.id],
                isExpanded: true, // Ensure parent is expanded to show the new child
              };
            }
            return r;
          });
          return [...updatedRows, newRow];
        });
        // Auto-expand parent
        setExpandedIds((prev) => new Set(prev).add(parentId));
      } else {
        setRows((prev) => [...prev, newRow]);
      }

      // Capture values for the setTimeout closure (these are stable local variables)
      const newRowId = newRow.id;
      const editColumnKey = targetColumn?.key;
      const editValues = { ...initialEditingValues };

      // Set editing state after React processes the row addition
      // Using setTimeout(0) ensures this runs after the current call stack completes
      // and React has batched/committed the setRows update
      if (firstEditableColumn && editColumnKey) {
        setTimeout(() => {
          setEditingRowId(newRowId);
          setEditingValues(editValues);
          editingValuesRef.current = editValues;
          setFocusedCell({
            rowId: newRowId,
            columnKey: editColumnKey,
          });
        }, 0);
      } else if (editColumnKey) {
        // No editable columns, just set focus
        setTimeout(() => {
          setFocusedCell({
            rowId: newRowId,
            columnKey: editColumnKey,
          });
        }, 0);
      }

      onRowCreate?.(parentId, rowType);

      // Return the new row ID for any additional handling
      return newRow.id;
    },
    [rows, rowsById, columns, onRowCreate, getNewRowDefaultData],
  );

  const handleDeleteRow = useCallback(
    (rowId: string) => {
      // Use setRows with callback to get the current state and avoid stale closure issues
      setRows((prev) => {
        // Build a fresh rowsById map from current state
        const currentRowsById = new Map(prev.map((r) => [r.id, r]));
        const row = currentRowsById.get(rowId);
        if (!row) return prev; // Row not found, return unchanged

        // Get all descendants
        const descendants = getDescendantIds(rowId, currentRowsById);
        const toDelete = [rowId, ...descendants];

        let updatedRows = prev.filter((r) => !toDelete.includes(r.id));

        // Update parent's childrenIds if row had a parent
        if (row.parentId) {
          updatedRows = updatedRows.map((r) =>
            r.id === row.parentId
              ? {
                  ...r,
                  childrenIds: r.childrenIds.filter((id) => id !== rowId),
                }
              : r,
          );
        }

        // Side effects - these are safe to call from within setRows callback
        // They won't cause issues because they update different state slices

        // Remove from selection
        setSelectedIds((prevSelected) => {
          const newSelectedIds = new Set(prevSelected);
          toDelete.forEach((id) => newSelectedIds.delete(id));
          onSelectionChange?.(Array.from(newSelectedIds));
          return newSelectedIds;
        });

        // Clear editing if deleting current row
        setEditingRowId((prevEditingId) => {
          if (prevEditingId && toDelete.includes(prevEditingId)) {
            setEditingValues({});
            editingValuesRef.current = {};
            return null;
          }
          return prevEditingId;
        });

        // Clear validation errors
        setValidationErrors((prevErrors) => {
          const newErrors = new Map(prevErrors);
          toDelete.forEach((id) => newErrors.delete(id));
          return newErrors;
        });

        onRowDelete?.(rowId);

        return updatedRows;
      });
    },
    [onRowDelete, onSelectionChange],
  );

  const handleMoveRow = useCallback(
    (rowId: string, newParentId: string | null, targetIndex: number) => {
      // Pre-validate the move using the current rows snapshot so that we can
      // fire onRowMove / onSelectionChange synchronously.  The setRows updater
      // runs asynchronously in React 18, so we cannot rely on a flag set inside
      // it to guard subsequent synchronous calls.
      const localRowsById = new Map(rows.map((r) => [r.id, r]));
      if (!localRowsById.get(rowId)) {
        console.error("Move failed: Row not found");
        return;
      }
      const descendants = getDescendantIds(rowId, localRowsById);
      if (
        newParentId === rowId ||
        (newParentId && descendants.includes(newParentId))
      ) {
        console.error("Move failed: Would create a cycle");
        return;
      }

      // Apply the move inside the updater using copies – never mutate the real
      // state objects (critical for React 18 Strict Mode double-render).
      setRows((prevRows) => {
        const rowsCopy = prevRows.map((r) => ({
          ...r,
          childrenIds: [...r.childrenIds],
        }));
        const result = moveRow(rowId, newParentId, targetIndex, rowsCopy);
        if (!result.success) {
          console.error("Move failed during apply:", result.error);
          return prevRows;
        }
        return buildTree(rowsCopy);
      });

      // Update selection states
      const newSelectedIds = handleSelectionAfterMove(
        rowId,
        selectedIds,
        rowsById,
        true,
      );
      setSelectedIds(newSelectedIds);

      onRowMove?.(rowId, newParentId, targetIndex);
      onSelectionChange?.(Array.from(newSelectedIds));
    },
    [rows, rowsById, selectedIds, onRowMove, onSelectionChange],
  );

  const handleIndent = useCallback(
    (rowId: string) => {
      const row = rowsById.get(rowId);
      const siblings = rows
        .filter((r) => r.parentId === row?.parentId)
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      const currentIndex = siblings.findIndex((r) => r.id === rowId);
      const newParent = currentIndex > 0 ? siblings[currentIndex - 1] : null;

      // Create copies to avoid direct React state mutation
      const rowsCopy = rows.map((r) => ({
        ...r,
        childrenIds: [...r.childrenIds],
      }));
      const result = indentRow(rowId, rowsCopy, maxDepth);

      if (!result.success) {
        console.warn("Indent failed:", result.error);
        return;
      }

      setRows(buildTree(rowsCopy));
      if (newParent && onRowMove) {
        // childrenIds length was updated by indentRow on the copy
        const newParentCopy = rowsCopy.find((r) => r.id === newParent.id);
        onRowMove(
          rowId,
          newParent.id,
          (newParentCopy?.childrenIds.length ?? 1) - 1,
        );
      }
    },
    [rows, rowsById, maxDepth, onRowMove],
  );

  const handleOutdent = useCallback(
    (rowId: string) => {
      const row = rowsById.get(rowId);
      const parent = row?.parentId ? rowsById.get(row.parentId) : null;
      const grandparentId = parent?.parentId ?? null;
      const parentSiblings = parent
        ? rows.filter((r) => r.parentId === grandparentId)
        : [];
      parentSiblings.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      const parentIndex = parent
        ? parentSiblings.findIndex((r) => r.id === parent.id)
        : -1;

      // Create copies to avoid direct React state mutation
      const rowsCopy = rows.map((r) => ({
        ...r,
        childrenIds: [...r.childrenIds],
      }));
      const result = outdentRow(rowId, rowsCopy);

      if (!result.success) {
        console.warn("Outdent failed:", result.error);
        return;
      }

      setRows(buildTree(rowsCopy));
      if (onRowMove && parentIndex >= 0) {
        onRowMove(rowId, grandparentId, parentIndex + 1);
      }
    },
    [rows, rowsById, onRowMove],
  );

  // Drag and drop operations
  const handleDragStart = useCallback(
    (rowId: string) => {
      if (!enableDragDrop) return;

      setDragState({
        draggedRowId: rowId,
        dropTargetId: null,
        dropPosition: "after",
        canDrop: false,
      });
    },
    [enableDragDrop],
  );

  const handleDragOver = useCallback(
    (targetRowId: string, position: "before" | "after" | "child") => {
      if (!dragState) return;

      const canDrop =
        dragState.draggedRowId !== targetRowId &&
        !getDescendantIds(dragState.draggedRowId, rowsById).includes(
          targetRowId,
        );

      setDragState({
        ...dragState,
        dropTargetId: targetRowId,
        dropPosition: position,
        canDrop,
      });
    },
    [dragState, rowsById],
  );

  const handleDrop = useCallback(() => {
    if (!dragState || !dragState.canDrop || !dragState.dropTargetId) return;

    const { draggedRowId, dropTargetId, dropPosition } = dragState;
    const targetRow = rowsById.get(dropTargetId);

    if (!targetRow) {
      setDragState(null);
      return;
    }

    let newParentId: string | null;
    let targetIndex: number;

    if (dropPosition === "child") {
      newParentId = dropTargetId;
      targetIndex = 0;
    } else {
      newParentId = targetRow.parentId;
      const siblings = rows.filter((r) => r.parentId === newParentId);
      siblings.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      const targetIdx = siblings.findIndex((r) => r.id === dropTargetId);
      targetIndex = dropPosition === "before" ? targetIdx : targetIdx + 1;
    }

    handleMoveRow(draggedRowId, newParentId, targetIndex);
    setDragState(null);
  }, [dragState, rowsById, rows, handleMoveRow]);

  const handleDragEnd = useCallback(() => {
    setDragState(null);
  }, []);

  // Pagination
  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage < 1 || newPage > totalPages) return;
      setPage(newPage);
      config.onPageChange?.(newPage);
    },
    [totalPages, config],
  );

  // Navigation helpers
  const getNavigationTarget = useCallback(
    (
      direction: "up" | "down" | "left" | "right",
      fromRowId: string,
      fromColumnKey: string,
    ): { rowId: string; columnKey: string } | null => {
      const currentRowIndex = visibleRows.findIndex((r) => r.id === fromRowId);
      // Only consider visible (non-hidden) columns for navigation so that
      // left/right never land on a column that has no rendered cell.
      const visibleColumns = columns.filter((c) => !c.hidden);
      const currentColumnIndex = visibleColumns.findIndex(
        (c) => c.key === fromColumnKey,
      );

      // Fallback: make sure the row is in the visible set
      if (currentRowIndex === -1) return null;
      // If the starting column is not visible, find the first visible column instead
      const safeColumnIndex =
        currentColumnIndex !== -1 ? currentColumnIndex : 0;

      switch (direction) {
        case "up":
          if (currentRowIndex > 0) {
            return {
              rowId: visibleRows[currentRowIndex - 1].id,
              columnKey: fromColumnKey,
            };
          }
          break;
        case "down":
          if (currentRowIndex < visibleRows.length - 1) {
            return {
              rowId: visibleRows[currentRowIndex + 1].id,
              columnKey: fromColumnKey,
            };
          }
          break;
        case "left":
          if (safeColumnIndex > 0) {
            return {
              rowId: fromRowId,
              columnKey: visibleColumns[safeColumnIndex - 1].key,
            };
          }
          break;
        case "right":
          if (safeColumnIndex < visibleColumns.length - 1) {
            return {
              rowId: fromRowId,
              columnKey: visibleColumns[safeColumnIndex + 1].key,
            };
          }
          break;
      }

      return null;
    },
    [visibleRows, columns],
  );

  return {
    // State
    rows,
    visibleRows,
    rowsById,
    selectedIds,
    expandedIds,
    editingRowId,
    editingValues,
    focusedCell,
    dragState,
    validationErrors,
    page,
    totalPages,
    allSelected,
    someSelected,

    // Expansion
    handleToggleExpand,
    handleExpandAll,
    handleCollapseAll,

    // Selection
    handleToggleSelection,
    handleSelectAll,
    handleClearSelection,
    getSelectionState,

    // Editing
    handleStartEdit,
    handleCancelEdit,
    handleCommitEdit,
    handleCellValueChange,
    handleMultiFieldChange,
    setFocusedCell,

    // Rows
    handleCreateRow,
    handleDeleteRow,
    handleMoveRow,
    handleIndent,
    handleOutdent,

    // Drag & Drop
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,

    // Pagination
    handlePageChange,

    // Navigation
    getNavigationTarget,
  };
}
